import {
  ShiftManagementAreasLocationsEmployeesDocument,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { SHIFT_CONFLICTS } from 'modules/shift_management/hooks/reducers/filter'
import {
  MAX_ITEMS_PER_PAGE,
  MAX_WORKAREAS_PER_PAGE,
} from 'modules/shift_management/hooks/reducers/pagination'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useOrderOptions } from 'modules/shift_management/hooks/useOrderEmployees'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import ShiftsRow, {
  ShiftsRowType,
  TShiftsRow,
  TWorkAreaShiftsRow,
} from 'modules/shift_management/store/internal/models/ShiftsRow'

const useGetShiftsRows = (): { shiftsRows: ShiftsRow[]; loading: boolean } => {
  const [filters] = useFilters()
  const [{ page }] = usePagination()
  const [{ view }] = useSchedule()
  const { startAt, endAt } = view
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()
  const currentPage = filters.status_id === SHIFT_CONFLICTS ? null : page
  const { selectableEmployeeIds } = useSelectableEmployees(view.startAt, view.endAt, 'read')

  const filteredEmployeeIds = useUnsafeMemo(() => {
    const requestedIds = filters.employee_ids?.map(Number)
    if (!requestedIds || requestedIds.length === 0) {
      return selectableEmployeeIds.length > 0 ? selectableEmployeeIds : null
    }
    return requestedIds.filter((id) => selectableEmployeeIds.includes(id))
  }, [filters.employee_ids, selectableEmployeeIds])

  const variables = {
    contractOnDate: startAt,
    orderBy: localStorageOrderEmployeesBy,
    orderDir: localStorageOrderEmployeesDir,
    page: currentPage,
    perPage: MAX_ITEMS_PER_PAGE,
    customFilters: filters.custom_filters,
    // - Accept employeeIds to refresh some isolated rows on update/delete
    employeeIds: filteredEmployeeIds,
    endAt,
    endAtISO: endAt,
    logicOperator: filters.logic_operator,
    shiftIds: filters.shift_ids,
    startAt,
    startAtISO: startAt,
    terminated: filters.terminated,
    locationIds: filters.locationIds,
  }

  const { loading: employeesLoading, data: employeesData } = useQuery(
    ShiftManagementMainPageDocument,
    {
      variables: {
        ...variables,
        perPage: MAX_ITEMS_PER_PAGE,
      },
      skip: !view.isByEmployees,
    }
  )

  const { loading: workAreasLoading, data: workAreasData } = useQuery(
    ShiftManagementAreasViewPageDocument,
    {
      variables: {
        ...variables,
        perPage: MAX_WORKAREAS_PER_PAGE,
      },
      skip: !view.isByAreas,
    }
  )

  const { loading: locationsEmployeesLoading, data: locationsEmployeesData } = useQuery(
    ShiftManagementAreasLocationsEmployeesDocument,
    {
      variables: {
        endAt,
        endAtISO: endAt,
        startAt,
        startAtISO: startAt,
        employeeIds: filteredEmployeeIds,
      },
      skip: !view.isByAreas,
    }
  )

  const shiftsRows = useUnsafeMemo(() => {
    if (view.isByEmployees) {
      const result = employeesData?.shiftManagement?.employeesPermissionsConnection?.nodes || []
      return result.map((shiftsRow: TShiftsRow) =>
        ShiftsRow.buildFromGraphQL(shiftsRow, ShiftsRowType.employee)
      )
    } else {
      const data = workAreasData?.shiftManagement?.workAreasConnection?.nodes || []
      const locations = locationsEmployeesData?.locations?.locations || []
      const employees = locationsEmployeesData?.shiftManagement?.employeesPermissions || []
      return compact(
        data.map((shiftsRow: TWorkAreaShiftsRow) => {
          const location = locations.find((l) => l.id === shiftsRow.location?.id)
          if (!location) return
          return ShiftsRow.buildFromWorkArea(shiftsRow, location, employees)
        })
      )
    }
  }, [
    view.isByEmployees,
    employeesData?.shiftManagement.employeesPermissionsConnection.nodes,
    workAreasData?.shiftManagement.workAreasConnection.nodes,
    locationsEmployeesData?.locations?.locations,
    locationsEmployeesData?.shiftManagement.employeesPermissions,
  ])

  if (view.isByAreas) return { shiftsRows, loading: workAreasLoading || locationsEmployeesLoading }

  return { shiftsRows, loading: employeesLoading }
}

export default useGetShiftsRows
